import { useForm } from '@mantine/form';
import useMaintenanceServiceImages from '../../../hooks/maintenance-services/useMaintenanceServiceImages.ts';
import useAddMaintenanceServiceImage from '../../../hooks/maintenance-services/useAddMaintenanceServiceImage.ts';
import useRemoveMaintenanceServiceImage from '../../../hooks/maintenance-services/useRemoveMaintenanceServiceImage.ts';
import {
  Button,
  Flex,
  Group,
  Image,
  LoadingOverlay,
  Stack,
  Text,
} from '@mantine/core';
import {
  IconCloudUpload,
  IconPlus,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { Dropzone, FileWithPath, MIME_TYPES } from '@mantine/dropzone';
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import useMaintenanceService from '../../../hooks/maintenance-services/useMaintenanceService.ts';
import {
  AddMaintenanceServiceImageRequest,
  RemoveMaintenanceServiceImageRequest,
} from '../../../api/requests/maintenance-service-request.ts';
import { notifyError } from '../../../utils/NotifyError.tsx';

export interface MutateMaintenanceServiceImagesFormValues {
  maintenanceServiceId: string;
}

interface MutateMaintenanceServiceImagesFormProps {
  initialValues: MutateMaintenanceServiceImagesFormValues;
  onSubmit: (formValues: MutateMaintenanceServiceImagesFormValues) => void;
  onCancel?: () => void;
  isSubmitting?: boolean;
  submitButtonText?: string;
  isUpdating: boolean;
}

export default function MutateMaintenanceServiceImagesForm({
  initialValues,
  onSubmit,
  onCancel,
  isSubmitting,
  isUpdating,
}: MutateMaintenanceServiceImagesFormProps) {
  const form = useForm<MutateMaintenanceServiceImagesFormValues>({
    mode: 'controlled',
    initialValues: {
      maintenanceServiceId: initialValues.maintenanceServiceId,
    },
  });

  const queryClient = useQueryClient();

  const { data: maintenanceService, isLoading: isMaintenanceServiceLoading } =
    useMaintenanceService(initialValues.maintenanceServiceId);

  const maintenanceServiceImageQueries = useMaintenanceServiceImages(
    maintenanceService?.id,
    maintenanceService?.imageIds,
  );

  const isAnyImageLoading = maintenanceServiceImageQueries.some(
    (q) => q.isLoading,
  );

  const { mutateAsync: addMaintenanceServiceImage } =
    useAddMaintenanceServiceImage();
  const { mutateAsync: removeMaintenanceServiceImage } =
    useRemoveMaintenanceServiceImage();

  const handleMaintenanceServiceImageRemoveClick = async (q) => {
    try {
      const removeMaintenanceServiceImageRequest: RemoveMaintenanceServiceImageRequest =
        {
          serviceId: maintenanceService.id,
          imageId: q.data.imageId.toString().replaceAll('/', '%2F'),
        };
      await removeMaintenanceServiceImage(removeMaintenanceServiceImageRequest);
      await queryClient.invalidateQueries({
        queryKey: ['maintenanceServices', maintenanceService.id],
        exact: false,
      });
    } catch (error) {
      notifyError('刪除失敗', error.message);
    }
  };

  const handleMaintenanceServiceImageAddClick = async (
    files: FileWithPath[],
  ) => {
    if (files.length === 0) return;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const addMaintenanceServiceImageRequest: AddMaintenanceServiceImageRequest =
          {
            serviceId: maintenanceService.id,
            file: formData,
          };
        await addMaintenanceServiceImage(addMaintenanceServiceImageRequest);
        await queryClient.invalidateQueries({
          queryKey: ['maintenanceServices', maintenanceService.id],
          exact: false,
        });
      } catch (error) {
        notifyError('上傳失敗', error.message);
      }
    }
  };

  const handleSubmit = form.onSubmit((values) => {
    onSubmit(values);
  });

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  if (isMaintenanceServiceLoading || isAnyImageLoading)
    return <LoadingOverlay visible={false} />;

  return (
    <>
      <form onSubmit={handleSubmit}>
        {maintenanceServiceImageQueries?.length ? (
          <Flex
            gap={9}
            style={{
              minHeight: 160,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              overflowX: 'hidden',
              overflowY: 'auto',
              alignContent: 'flex-start',
            }}
          >
            {maintenanceServiceImageQueries?.map((q) => (
              <div
                key={q.data.imageId}
                style={{
                  width: 150,
                  height: 150,
                  borderRadius: 8,
                  position: 'relative',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <Image
                  src={q.data.signedUrl}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                {/* 刪除按鈕 */}
                <Button
                  size="xs"
                  variant="filled"
                  color="red"
                  style={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    padding: '2px 6px',
                    zIndex: 2,
                  }}
                  onClick={() => handleMaintenanceServiceImageRemoveClick(q)}
                >
                  ×
                </Button>
              </div>
            ))}

            {/* 新增圖片按鈕 */}
            <div
              style={{
                width: 150,
                height: 150,
                borderRadius: 8,
                backgroundColor: '#F8F9FA',
                alignContent: 'center',
                flexShrink: 0,
                cursor: 'pointer',
              }}
            >
              <Dropzone
                onDrop={(files) => handleMaintenanceServiceImageAddClick(files)}
                onReject={(files) => console.log('rejected files', files)}
                maxSize={5 * 1024 ** 2}
                accept={[MIME_TYPES.png]}
                style={{
                  width: '100%',
                  height: '100%',
                  border: '1px dashed var(--mantine-color-gray-4)',
                  borderRadius: 8,
                  padding: 0,
                }}
                styles={{
                  inner: {
                    height: '100%',
                  },
                }}
              >
                <Group justify="center" style={{ height: '100%' }}>
                  <Stack align="center">
                    <IconPlus size={24} color="#868E96" />
                    <Text
                      style={{
                        fontSize: 16,
                        fontWeight: 500,
                        color: '#868E96',
                      }}
                    >
                      新增圖片
                    </Text>
                  </Stack>
                </Group>
              </Dropzone>
            </div>
          </Flex>
        ) : (
          <Dropzone
            onDrop={(files) => handleMaintenanceServiceImageAddClick(files)}
            onReject={(files) => console.log('rejected files', files)}
            maxSize={5 * 1024 ** 2}
            accept={[MIME_TYPES.png]}
            style={{
              border: '1px dashed var(--mantine-color-gray-4)',
              borderRadius: 8,
              padding: 0,
              height: 221,
            }}
          >
            <Group justify="center" gap="xl" mih={220}>
              <Stack align="center">
                <Dropzone.Accept>
                  <IconUpload size={52} stroke={1.5} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={52} stroke={1.5} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconCloudUpload size={52} color="#212529" stroke={1.5} />
                </Dropzone.Idle>
                <div>
                  <Text size="lg" fw={500} inline mb={12} ta="center">
                    上傳圖片
                  </Text>
                  <Text size="sm" c="dimmed" inline mt={7} ta="center">
                    把 .png 檔案拖曳到這裡上傳,或是選擇圖片。
                  </Text>
                </div>
              </Stack>
            </Group>
          </Dropzone>
        )}

        <Group justify="flex-end" mt="24px">
          <Button variant="outline" onClick={handleCancel}>
            取消
          </Button>
          <Button
            type="submit"
            loading={isSubmitting}
            loaderProps={{ type: 'oval', size: 'xs' }}
          >
            {isUpdating ? '更新' : '下一步'}
          </Button>
        </Group>
      </form>
    </>
  );
}
